import type React from 'react'
import { useCallback, useRef, useState } from 'react'
import Cropper, { ReactCropperElement } from 'react-cropper'
import { CgSpinner } from 'react-icons/cg'
import { FiImage, FiUpload, FiX } from 'react-icons/fi'

import { Button } from '~/components/Button'
import gpaLogo from '~/assets/img/gpaLogo.png'
import { AsyncLogoImage } from '~/components/LogoInput/AsyncLogoImage'
import {
  fetchPersonalTrainerLogoUrl,
  uploadPersonalTrainerLogo,
} from '~/modules/personalTrainer/services'
import { insertToast } from '~/modules/toasts/services'
import 'cropperjs/dist/cropper.css'
import './cropperOverrides.css'

interface LogoInputProps {
  personalTrainerId: string
  currentLogoUrl?: string
  onUploadSuccess?: (logoUrl: string) => void
  className?: string
}

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
const DEFAULT_LOGO_URL = gpaLogo

export const LogoInput: React.FC<LogoInputProps> = ({
  personalTrainerId: _personalTrainerId,
  currentLogoUrl,
  onUploadSuccess,
  className = '',
}) => {
  const [preview, setPreview] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [sourceImage, setSourceImage] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [logoFetchKey, setLogoFetchKey] = useState(0)
  const [logoLoading, setLogoLoading] = useState(true)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cropperRef = useRef<ReactCropperElement>(null)

  const fetchSignedLogoUrl = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await fetchPersonalTrainerLogoUrl(signal)
      // Handle both nested and top-level signedUrl shapes
      return (
        response.logo?.signedUrl ||
        response.logo?.publicUrl ||
        (response as any)?.signedUrl ||
        response.logoUrl ||
        null
      )
    } catch {
      return null
    }
  }, [])

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      // Validate file type
      if (!ALLOWED_TYPES.includes(file.type)) {
        insertToast({
          title: 'Formato inválido',
          message: 'Use apenas arquivos JPEG, JPG, PNG ou WebP',
          duration: 3,
          type: 'error',
        })
        return
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        insertToast({
          title: 'Arquivo muito grande',
          message: 'O tamanho máximo é 5MB',
          duration: 3,
          type: 'error',
        })
        return
      }

      setSelectedFile(null)
      setPendingFile(file)

      // Create preview for cropper
      const reader = new FileReader()
      reader.onloadend = () => {
        setSourceImage(reader.result as string)
      }
      reader.readAsDataURL(file)
    },
    [],
  )

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return

    setUploading(true)
    try {
      const result = await uploadPersonalTrainerLogo(selectedFile)
      const latestLogoUrl =
        result.logo?.signedUrl ||
        result.logo?.publicUrl ||
        (result as any)?.signedUrl ||
        result.logoUrl ||
        DEFAULT_LOGO_URL
      if (latestLogoUrl) {
        insertToast({
          title: 'Sucesso!',
          message: 'Logo enviado com sucesso',
          duration: 3,
          type: 'success',
        })
        setPreview(null)
        setSelectedFile(null)
        setLogoFetchKey((key) => key + 1) // refetch image
        onUploadSuccess?.(latestLogoUrl)
      }
    } catch (error) {
      insertToast({
        title: 'Erro ao enviar',
        message: 'Não foi possível enviar o logo',
        duration: 3,
        type: 'error',
      })
    } finally {
      setUploading(false)
    }
  }, [selectedFile, onUploadSuccess])

  const handleRemoveSelection = useCallback(() => {
    setSelectedFile(null)
    setPreview(null)
    setPendingFile(null)
    setSourceImage(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const handleCancelCrop = useCallback(() => {
    setPendingFile(null)
    setSourceImage(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const handleConfirmCrop = useCallback(() => {
    if (!pendingFile) return

    const cropper = cropperRef.current?.cropper
    if (!cropper) return

    const canvas = cropper.getCroppedCanvas({
      width: 800,
      height: 600,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    })

    if (!canvas) {
      insertToast({
        title: 'Erro ao processar imagem',
        message: 'Não foi possível gerar o recorte',
        type: 'error',
        duration: 4,
      })
      return
    }

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          insertToast({
            title: 'Erro ao processar imagem',
            message: 'Não foi possível gerar o recorte',
            type: 'error',
            duration: 4,
          })
          return
        }

        const ext = pendingFile.name.split('.').pop() || 'jpg'
        const croppedFile = new File([blob], `logo-crop.${ext}`, {
          type: blob.type || pendingFile.type,
        })
        const croppedPreview = canvas.toDataURL(blob.type || pendingFile.type)

        setSelectedFile(croppedFile)
        setPreview(croppedPreview)
        setPendingFile(null)
        setSourceImage(null)
      },
      pendingFile.type || 'image/jpeg',
      0.92,
    )
  }, [pendingFile])

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <div className='flex flex-col gap-2'>
        <span className='font-semibold text-gpa-gray-800'>Logo do Personal Trainer</span>
        <span className='text-sm text-gpa-gray-600'>
          Formatos aceitos: JPEG, PNG, WebP (máx. 5MB)
        </span>
        <span className='text-sm text-gpa-gray-600'>
          Ajuste a imagem no enquadramento 4:3 movendo-a dentro da área fixa.
        </span>
      </div>

      {/* Logo Preview */}
      <div className='flex items-center justify-center w-full'>
        <div className='relative w-full max-w-[420px] aspect-[4/3] border-2 border-dashed border-gpa-gray-300 rounded-lg overflow-hidden bg-gpa-gray-50 flex items-center justify-center'>
          {sourceImage ? (
            <Cropper
              ref={cropperRef}
              style={{ height: '100%', width: '100%' }}
              className='logo-cropper w-full h-full bg-white'
              zoomable
              guides
              dragMode='move'
              viewMode={1}
              autoCropArea={1}
              background={false}
              cropBoxMovable={false}
              cropBoxResizable={false}
              aspectRatio={4 / 3}
              initialAspectRatio={4 / 3}
              src={sourceImage}
            />
          ) : preview ? (
            <img
              src={preview}
              alt='Logo preview'
              className='w-full h-full object-contain'
              loading='eager'
              {...({ fetchPriority: 'high' } as React.ImgHTMLAttributes<HTMLImageElement>)}
              onError={() => setPreview(null)}
            />
          ) : (
            <>
              {logoLoading && (
                <div className='absolute inset-0 w-full h-full bg-gpa-gray-100 animate-pulse' />
              )}
              <AsyncLogoImage
                key={logoFetchKey}
                className='w-full h-full object-contain'
                fallbackSrc={DEFAULT_LOGO_URL}
                initialSrc={currentLogoUrl}
                fetchSrc={fetchSignedLogoUrl}
                refreshKey={logoFetchKey}
                onLoadingChange={setLogoLoading}
              />
            </>
          )}
        </div>
      </div>

      {/* File Selection */}
      <div className='flex flex-col gap-3'>
        <input
          ref={fileInputRef}
          type='file'
          accept='image/jpeg,image/jpg,image/png,image/webp'
          onChange={handleFileSelect}
          className='hidden'
          disabled={uploading}
        />

        {sourceImage ? (
          <div className='flex flex-col gap-2'>
            <div className='flex items-center justify-between p-3 bg-gray-100 rounded-lg border border-gray-200'>
              <div className='flex items-center gap-2 flex-1 min-w-0'>
                <FiImage size={20} className='text-gpa-blue-500 flex-shrink-0' />
                <span className='text-sm text-gpa-gray-800 truncate'>
                  Ajuste o enquadramento e confirme o recorte
                </span>
              </div>
              <button
                type='button'
                onClick={handleCancelCrop}
                className='ml-2 p-1 hover:bg-gpa-blue-100 rounded transition-colors'
              >
                <FiX size={20} className='text-gpa-gray-600' />
              </button>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
              <Button
                type='button'
                mode='outlined'
                onClick={handleCancelCrop}
                disabled={uploading}
                className='w-full'
              >
                Cancelar
              </Button>
              <Button
                type='button'
                mode='filled'
                onClick={handleConfirmCrop}
                disabled={uploading}
                className='w-full'
              >
                Confirmar recorte 4:3
              </Button>
            </div>
          </div>
        ) : selectedFile ? (
          <div className='flex flex-col gap-2'>
            <div className='flex items-center justify-between p-3 bg-gpa-blue-50 rounded-lg border border-gpa-blue-200'>
              <div className='flex items-center gap-2 flex-1 min-w-0'>
                <FiImage size={20} className='text-gpa-blue-500 flex-shrink-0' />
                <span className='text-sm text-gpa-gray-800 truncate'>
                  {selectedFile.name}
                </span>
                <span className='text-xs text-gpa-gray-600 flex-shrink-0'>
                  ({(selectedFile.size / 1024).toFixed(0)} KB)
                </span>
              </div>
              {!uploading && (
                <button
                  type='button'
                  onClick={handleRemoveSelection}
                  className='ml-2 p-1 hover:bg-gpa-blue-100 rounded transition-colors'
                >
                  <FiX size={20} className='text-gpa-gray-600' />
                </button>
              )}
            </div>

            <Button
              type='button'
              mode='filled'
              onClick={handleUpload}
              disabled={uploading}
              className='w-full'
            >
              {uploading ? (
                <div className='flex items-center gap-2'>
                  <CgSpinner size={20} className='animate-spin' />
                  <span>Enviando...</span>
                </div>
              ) : (
                <div className='flex items-center gap-2'>
                  <FiUpload size={20} />
                  <span>Enviar Logo</span>
                </div>
              )}
            </Button>
          </div>
        ) : (
          <Button
            type='button'
            mode='outlined'
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className='w-full'
          >
            <div className='flex items-center gap-2'>
              <FiUpload size={20} />
              <span>Selecionar Arquivo</span>
            </div>
          </Button>
        )}
      </div>
    </div>
  )
}
